/**
 * Library domain types, runtime guards, and credentialed fetch helpers.
 *
 * Module: lib/library
 * Layer:  util / data-access
 *
 * Purpose:
 *     Defines the catalog / checkout / assignment shapes the library page
 *     renders, validates every API row at the network boundary with
 *     hand-written runtime guards (the project deliberately does NOT declare
 *     zod — see lib/student-assignments), and exposes credentialed fetch
 *     helpers. List/read helpers self-heal to safe empty values so the page
 *     always renders; mutation helpers surface failures so callers can react.
 *
 * AI context:
 *     - Entry points called by: src/app/library/** and
 *       src/components/library/** (BookCard, page controller hook).
 *     - Depends on: the browser `fetch` API only (matches lib/leaderboard,
 *       lib/student-assignments).
 *     - Side effects: network I/O against the `/api/library/*` BFF routes.
 *     - Invariants: every returned row passed its guard; malformed rows are
 *       dropped, never coerced. List/read helpers never throw; mutation
 *       helpers reject so the UI can show an error and refresh.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Responses are untrusted JSON. The guards are the trust boundary: only
 *       rows with the exact expected primitive shape survive, so the UI never
 *       renders attacker-controlled object keys. Cover URLs are passed to
 *       next/image, which only optimizes configured remote hosts.
 */

import type { TranslationKey } from './i18n';

/** Book media type as stored by the backend `BookType` enum. */
export type BookType = 'ebook' | 'audiobook' | 'interactive' | 'physical' | 'video';

/** How a book is made available to a student. */
export type AccessModel = 'unlimited' | 'checkout' | 'assigned';

/** Lifecycle status of a checkout transaction. */
export type CheckoutStatus = 'checked_out' | 'returned' | 'overdue' | 'lost';

/** A single catalog entry as shown in the library grid. */
export interface LibraryBook {
  readonly id: number;
  readonly isbn: string | null;
  readonly title: string;
  readonly subtitle: string | null;
  readonly authors: readonly string[];
  readonly publisher: string | null;
  readonly description: string | null;
  readonly book_type: BookType;
  readonly cover_url: string | null;
  readonly reading_levels: Readonly<Record<string, unknown>>;
  readonly available_copies: number;
  readonly total_copies: number;
  readonly access_model: AccessModel;
  readonly is_featured: boolean;
  readonly category: string | null;
}

/** A book the signed-in student currently has checked out. */
export interface BookCheckout {
  readonly id: number;
  readonly book_id: number;
  readonly book_title: string;
  readonly due_date: string;
  readonly status: CheckoutStatus;
  readonly checked_out_at: string;
}

/** A teacher-assigned reading task for the signed-in student. */
export interface ReadingAssignment {
  readonly id: number;
  readonly book_id: number;
  readonly book_title: string;
  readonly due_date: string | null;
  readonly assigned_at: string;
  readonly progress_percent: number;
}

/** Filters accepted by {@link fetchLibraryBooks}. */
export interface LibraryBookQuery {
  readonly search?: string;
  readonly bookType?: BookType;
  readonly category?: string;
}

/** Book type -> i18n badge label key (data-driven, avoids a switch). */
export const BOOK_TYPE_LABEL_KEY: Readonly<Record<BookType, TranslationKey>> = {
  ebook: 'library.type.ebook',
  audiobook: 'library.type.audiobook',
  interactive: 'library.type.interactive',
  physical: 'library.type.physical',
  video: 'library.type.video',
};

/** Book type -> Tailwind badge color classes (data-driven design tokens). */
export const BOOK_TYPE_BADGE_CLASS: Readonly<Record<BookType, string>> = {
  ebook: 'bg-blue-100 text-blue-700',
  audiobook: 'bg-purple-100 text-purple-700',
  interactive: 'bg-green-100 text-green-700',
  physical: 'bg-amber-100 text-amber-700',
  video: 'bg-rose-100 text-rose-700',
};

/** The accepted {@link BookType} values (single source of truth). */
const VALID_BOOK_TYPES: ReadonlySet<string> = new Set<BookType>([
  'ebook',
  'audiobook',
  'interactive',
  'physical',
  'video',
]);

/** The accepted {@link CheckoutStatus} values (single source of truth). */
const VALID_CHECKOUT_STATUSES: ReadonlySet<string> = new Set<CheckoutStatus>([
  'checked_out',
  'returned',
  'overdue',
  'lost',
]);

/** The accepted {@link AccessModel} values (single source of truth). */
const VALID_ACCESS_MODELS: ReadonlySet<string> = new Set<AccessModel>([
  'unlimited',
  'checkout',
  'assigned',
]);

/** Primitive `typeof` tag a required field must match. */
type PrimitiveTag = 'number' | 'string';

/**
 * Narrow an unknown JSON value to a non-null object record.
 *
 * @param value - A decoded JSON value.
 * @returns `true` when `value` is a plain, non-null object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Check that every required field on a row matches its expected primitive.
 *
 * @param row - The decoded object row.
 * @param required - Field name -> expected `typeof` tag (data-driven schema).
 * @returns `true` only when every listed field has the exact expected type.
 * @remarks Collapses the per-field `if` chains in the row guards into one
 *   table-driven check so each guard stays within the CC<=5 gate.
 */
function hasPrimitiveFields(
  row: Record<string, unknown>,
  required: Readonly<Record<string, PrimitiveTag>>,
): boolean {
  return Object.entries(required).every(([field, expected]) => typeof row[field] === expected);
}

/** Read a string field, falling back to null when absent or non-string. */
function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Coerce an unknown authors field into a readonly string array.
 *
 * @param value - The raw `authors` field (backend sends a JSON list).
 * @returns Only the string entries; an empty array when not an array.
 * @remarks Drops non-string entries instead of coercing, so the UI never
 *   renders `[object Object]` from a malformed row.
 */
function toStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * Read the first string element of a JSON array, or null.
 *
 * @param value - The raw field (backend sends `categories` as a JSON list).
 * @returns The first string entry, or null when absent/malformed.
 */
function firstCategory(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const first = value[0];
  return typeof first === 'string' ? first : null;
}

/**
 * Normalize a nullable numeric copy count into a non-negative integer.
 *
 * @param value - The raw `available_copies` / `copies_count` field.
 * @returns The number when finite and non-negative, else 0.
 * @remarks The list endpoint sends `null` for videos (no physical copies).
 */
function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/**
 * Narrow an unknown value to a {@link LibraryBook}.
 *
 * @param value - A single element of the decoded catalog array.
 * @returns `true` only when the row carries the required primitive fields and
 *   a recognized {@link BookType}.
 * @remarks Runtime trust boundary for the catalog API. Maps the backend
 *   `LibraryBookListResponse` shape (cover_image_url, copies_count) onto the
 *   public {@link LibraryBook} shape.
 */
/** Required primitive fields for a catalog row (data-driven schema). */
const BOOK_REQUIRED: Readonly<Record<string, PrimitiveTag>> = {
  id: 'number',
  title: 'string',
};

/** Resolve a raw access-model field to a recognized {@link AccessModel}. */
function toAccessModel(value: unknown): AccessModel {
  return typeof value === 'string' && VALID_ACCESS_MODELS.has(value)
    ? (value as AccessModel)
    : 'checkout';
}

/** Build a {@link LibraryBook} from an already-validated catalog row. */
function mapLibraryBook(value: Record<string, unknown>, bookType: BookType): LibraryBook {
  return {
    id: value['id'] as number,
    isbn: stringOrNull(value['isbn']),
    title: value['title'] as string,
    subtitle: stringOrNull(value['subtitle']),
    authors: toStringArray(value['authors']),
    publisher: stringOrNull(value['publisher']),
    description: stringOrNull(value['description']),
    book_type: bookType,
    cover_url: stringOrNull(value['cover_image_url']),
    reading_levels: isRecord(value['reading_levels']) ? value['reading_levels'] : {},
    available_copies: toCount(value['available_copies']),
    total_copies: toCount(value['copies_count']),
    access_model: toAccessModel(value['default_access_model']),
    is_featured: value['is_pinned'] === true,
    category: firstCategory(value['categories']),
  };
}

export function parseLibraryBook(value: unknown): LibraryBook | null {
  if (!isRecord(value) || !hasPrimitiveFields(value, BOOK_REQUIRED)) {
    return null;
  }
  const bookType = value['book_type'];
  if (typeof bookType !== 'string' || !VALID_BOOK_TYPES.has(bookType)) {
    return null;
  }
  return mapLibraryBook(value, bookType as BookType);
}

/**
 * Narrow an unknown value to a {@link BookCheckout}.
 *
 * @param value - A single element of the decoded checkouts array.
 * @returns `true` only when the row has the required fields and a recognized
 *   {@link CheckoutStatus}.
 * @remarks Maps the backend `BookCheckoutDetailResponse` (nested
 *   book_copy.book.title, checkout_date) onto the flat public shape.
 */
/** Required primitive fields for a checkout row (data-driven schema). */
const CHECKOUT_REQUIRED: Readonly<Record<string, PrimitiveTag>> = {
  id: 'number',
  due_date: 'string',
  checkout_date: 'string',
};

/** Extract the nested `book_copy.book` record from a checkout row. */
function nestedBook(value: Record<string, unknown>): Record<string, unknown> {
  const copy = isRecord(value['book_copy']) ? value['book_copy'] : {};
  return isRecord(copy['book']) ? copy['book'] : {};
}

/** Build a {@link BookCheckout} from an already-validated checkout row. */
function mapCheckout(value: Record<string, unknown>, status: CheckoutStatus): BookCheckout {
  const book = nestedBook(value);
  return {
    id: value['id'] as number,
    book_id: typeof book['id'] === 'number' ? book['id'] : 0,
    book_title: typeof book['title'] === 'string' ? book['title'] : '',
    due_date: value['due_date'] as string,
    status,
    checked_out_at: value['checkout_date'] as string,
  };
}

export function parseCheckout(value: unknown): BookCheckout | null {
  if (!isRecord(value) || !hasPrimitiveFields(value, CHECKOUT_REQUIRED)) {
    return null;
  }
  const status = value['status'];
  if (typeof status !== 'string' || !VALID_CHECKOUT_STATUSES.has(status)) {
    return null;
  }
  return mapCheckout(value, status as CheckoutStatus);
}

/**
 * Narrow an unknown value to a {@link ReadingAssignment}.
 *
 * @param value - A single element of the decoded assignments array.
 * @returns `true` only when the row has the required `id`, `book_id`, and
 *   `assigned_at` fields.
 * @remarks Maps the backend `ReadingAssignmentResponse` onto the public shape;
 *   `target_completion` is treated as the progress target percentage.
 */
/** Required primitive fields for an assignment row (data-driven schema). */
const ASSIGNMENT_REQUIRED: Readonly<Record<string, PrimitiveTag>> = {
  id: 'number',
  book_id: 'number',
  assigned_at: 'string',
};

/** Build a {@link ReadingAssignment} from an already-validated row. */
function mapAssignment(value: Record<string, unknown>): ReadingAssignment {
  const target = value['target_completion'];
  const book = isRecord(value['book']) ? value['book'] : {};
  return {
    id: value['id'] as number,
    book_id: value['book_id'] as number,
    book_title: typeof book['title'] === 'string' ? book['title'] : '',
    due_date: stringOrNull(value['due_date']),
    assigned_at: value['assigned_at'] as string,
    progress_percent: typeof target === 'number' && Number.isFinite(target) ? target : 0,
  };
}

export function parseAssignment(value: unknown): ReadingAssignment | null {
  if (!isRecord(value) || !hasPrimitiveFields(value, ASSIGNMENT_REQUIRED)) {
    return null;
  }
  return mapAssignment(value);
}

/**
 * Decode and validate a JSON array body, dropping malformed rows.
 *
 * @typeParam T - The validated element type.
 * @param body - The decoded JSON response body.
 * @param parse - A per-row guard returning the typed row or null.
 * @returns Every row that passed the guard (possibly empty).
 */
function parseArray<T>(body: unknown, parse: (value: unknown) => T | null): readonly T[] {
  if (!Array.isArray(body)) {
    return [];
  }
  const out: T[] = [];
  for (const row of body) {
    const parsed = parse(row);
    if (parsed !== null) {
      out.push(parsed);
    }
  }
  return out;
}

/** Build a credentialed request init, attaching an abort signal when given. */
function credentialedInit(signal?: AbortSignal): RequestInit {
  return signal ? { credentials: 'include', signal } : { credentials: 'include' };
}

/** Build the `/api/library/books` query string from the active filters. */
function buildBooksQuery(params: LibraryBookQuery): string {
  const search = new URLSearchParams();
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.bookType) {
    search.set('book_type', params.bookType);
  }
  if (params.category) {
    search.set('category', params.category);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch and validate the library catalog for the active filters.
 *
 * @param params - Optional search / type / category filters.
 * @param signal - Optional abort signal so callers can cancel stale loads.
 * @returns The validated catalog (malformed rows dropped); `[]` on any non-OK
 *   response or malformed body.
 * @remarks Never throws, so an unauthenticated or not-yet-seeded backend
 *   degrades to an empty grid rather than a crash.
 * @example
 *   const books = await fetchLibraryBooks({ bookType: 'ebook' }, signal);
 */
export async function fetchLibraryBooks(
  params: LibraryBookQuery = {},
  signal?: AbortSignal,
): Promise<readonly LibraryBook[]> {
  const path = `/api/library/books${buildBooksQuery(params)}`;
  const res = await fetch(path, credentialedInit(signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  return parseArray(body, parseLibraryBook);
}

/**
 * Fetch and validate the signed-in student's current checkouts.
 *
 * @param signal - Optional abort signal so callers can cancel stale loads.
 * @returns The validated checkouts; `[]` on any non-OK response (e.g. when the
 *   visitor is anonymous and the endpoint returns 401).
 * @remarks Never throws; the page only renders the section when non-empty.
 */
export async function fetchMyCheckouts(signal?: AbortSignal): Promise<readonly BookCheckout[]> {
  const res = await fetch('/api/library/checkouts', credentialedInit(signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  return parseArray(body, parseCheckout);
}

/**
 * Fetch and validate the signed-in student's reading assignments.
 *
 * @param signal - Optional abort signal so callers can cancel stale loads.
 * @returns The validated assignments; `[]` on any non-OK response.
 * @remarks Never throws; the page only renders the section when non-empty.
 */
export async function fetchMyAssignments(
  signal?: AbortSignal,
): Promise<readonly ReadingAssignment[]> {
  const res = await fetch('/api/library/assignments', credentialedInit(signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  return parseArray(body, parseAssignment);
}

/**
 * Check out a book for the signed-in student.
 *
 * @param bookId - The catalog id of the book to check out.
 * @returns Resolves when the checkout succeeds.
 * @throws Error when the response status is not ok (caller shows the error).
 * @remarks Mutation helper: unlike the list helpers it does NOT swallow
 *   failures, so the UI can surface "unavailable" / "already checked out".
 */
export async function checkoutBook(bookId: number): Promise<void> {
  const res = await fetch('/api/library/checkouts', {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ book_id: bookId }),
  });
  if (!res.ok) {
    throw new Error(`${String(res.status)} ${res.statusText}`);
  }
}

/**
 * Return a previously checked-out book.
 *
 * @param checkoutId - The id of the checkout transaction to close.
 * @returns Resolves when the return is recorded.
 * @throws Error when the response status is not ok (caller shows the error).
 * @remarks Mutation helper: surfaces failures so the UI can react.
 */
export async function returnBook(checkoutId: number): Promise<void> {
  const res = await fetch(`/api/library/checkouts/${String(checkoutId)}/return`, {
    credentials: 'include',
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`${String(res.status)} ${res.statusText}`);
  }
}
